import { cva } from "class-variance-authority";

import { PlanningType } from "@eisox/api/core-api";
import { Circle } from "@eisox/design-system";
import { LeafIcon, UserIcon } from "@eisox/icons";

import { PlanningTypeKey } from "~/features/planning";

interface UnusualAbsenceSchemeProps {
  absenceTemperature: number;
  preComfortTemperature?: number;
  comfortTemperature?: number;
}

// `border-r-2` would force `border-right-style: solid`, so width and style are both set as raw properties.
const DASHED_RIGHT_BORDER = "[border-right-width:2px] [border-right-style:dashed] border-r-primary-dark-blue";

const temperatureVariants = cva("flex min-h-[80px] items-center justify-center", {
  variants: {
    variant: {
      "comfort-8": `${DASHED_RIGHT_BORDER} border-t-2 border-t-primary-dark-blue`,
      "comfort-9": "flex-col border-t-2 border-r-2 border-t-primary-dark-blue border-r-primary-dark-blue",
      "comfort-10":
        "col-start-4 col-end-6 [border-top-width:2px] border-r-2 border-b-2 [border-top-style:dashed] border-t-primary-dark-blue border-r-primary-dark-blue border-b-primary-dark-blue",
      "comfort-12": "border-t-2 border-t-primary-dark-blue",
      "comfort-13": "border-t border-t-primary-gray",
      "pre-comfort-8": `${DASHED_RIGHT_BORDER} border-t border-b border-t-primary-gray border-b-primary-gray`,
      "pre-comfort-9": "border-t border-b border-t-primary-gray border-b-primary-gray",
      "pre-comfort-10": `${DASHED_RIGHT_BORDER} col-start-4 col-end-6 border-t border-b border-t-primary-dark-blue border-b-primary-gray`,
      "pre-comfort-11": `${DASHED_RIGHT_BORDER} border-t border-b border-t-primary-dark-blue border-b-primary-gray`,
      "pre-comfort-12": "border-t border-b border-t-primary-gray border-b-primary-gray",
      "pre-comfort-13": "border-t border-b border-t-primary-gray border-b-primary-gray",
    },
  },
});

export const UnusualAbsenceScheme: React.FC<UnusualAbsenceSchemeProps> = ({
  absenceTemperature,
  preComfortTemperature,
  comfortTemperature,
}) => (
  <div className="grid w-full grid-cols-[auto_1fr_1fr_1fr_1fr_1fr_42px] grid-rows-[auto_auto_auto]">
    <div className="col-span-1 row-span-2 row-start-1 row-end-3 flex flex-col justify-between">
      <PlanningTypeKey
        className="text-primary-dark-blue!"
        type={PlanningType.COMFORT}
        temperature={comfortTemperature}
      />
      <PlanningTypeKey
        className="text-primary-dark-blue!"
        type={PlanningType.PRE_COMFORT}
        temperature={preComfortTemperature}
      />
      <PlanningTypeKey
        className="text-primary-dark-blue!"
        type={PlanningType.ABSENCE}
        temperature={absenceTemperature}
      />
    </div>
    <div className={temperatureVariants({ variant: "comfort-8" })} />
    <div className={temperatureVariants({ variant: "comfort-9" })}>
      <div className="after:right-0 relative inline-block h-px w-[calc(100%-4px)] bg-primary-dark-gray before:absolute before:top-[-3px] before:-rotate-45 before:border before:border-t before:border-l before:border-primary-dark-gray before:py-[3px] before:content-[''] after:absolute after:top-[-3px] after:-rotate-45 after:border after:border-r after:border-b after:border-primary-dark-gray after:py-[3px] after:content-['']" />
      <p className="font-normal text-base leading-[21px] text-primary-dark-gray italic">1h</p>
    </div>
    <div className={temperatureVariants({ variant: "comfort-10" })}>
      <LeafIcon className="h-5 w-5 text-primary-green" />
    </div>
    <div className={temperatureVariants({ variant: "comfort-12" })} />
    <div className={temperatureVariants({ variant: "comfort-13" })} />
    <div className={temperatureVariants({ variant: "pre-comfort-8" })}>
      <Circle.Root className="text-primary-white">
        <UserIcon />
      </Circle.Root>
    </div>
    <div className={temperatureVariants({ variant: "pre-comfort-9" })}>
      <Circle.Root disabled className="text-primary-white">
        <UserIcon />
      </Circle.Root>
    </div>
    <div className={temperatureVariants({ variant: "pre-comfort-10" })}>
      <Circle.Root disabled className="text-primary-white">
        <UserIcon />
      </Circle.Root>
    </div>
    <div className={temperatureVariants({ variant: "pre-comfort-12" })}>
      <Circle.Root className="text-primary-white">
        <UserIcon />
      </Circle.Root>
    </div>
    <div className={temperatureVariants({ variant: "pre-comfort-13" })} />
    <div />
    {[...Array<number>(6)].map((_, index) => (
      <p key={index} className="mt-5 font-normal text-base leading-[21px] text-primary-dark-blue!">
        {index + 8}h
      </p>
    ))}
  </div>
);

UnusualAbsenceScheme.displayName = "UnusualAbsenceScheme";
